import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import dispatchMessage, { formatter } from 'app/main/apps/common/AuraFunctions';
import { reversePayment } from 'app/main/apps/e-commerce/store/ordersSlice';

export const MODE_MONEY_RETURNED = 'MONEY_RETURNED';
export const MODE_NEVER_RECEIVED = 'NEVER_RECEIVED';

/**
 * PGSS-5326 — reverse a payment that came back.
 *
 * The two modes are not interchangeable and the wording here is what stops them being picked at
 * random: one leaves a visible return on the ledger, the other hides an entry that should never
 * have existed.
 */
function PaymentReversalDialog({ open, payment, invoiceId, onClose, onReversed }) {
  const dispatch = useDispatch();
  const [mode, setMode] = useState(MODE_MONEY_RETURNED);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setMode(MODE_MONEY_RETURNED);
      setReason('');
      setSubmitting(false);
    }
  }, [open]);

  const trimmedReason = reason.trim();
  const canSubmit = trimmedReason.length > 0 && !submitting;

  const handleSubmit = () => {
    if (!canSubmit) {
      return;
    }
    setSubmitting(true);
    dispatch(reversePayment({
      invoiceId,
      paymentId: payment?.id,
      mode,
      reason: trimmedReason,
      // PGSS-5557: the list merges two tables whose ids are numbered separately, so the id alone
      // does not say which row is meant. The row carries its own source; pass it straight through.
      source: payment?.source,
    }))
      .unwrap()
      .then((payload) => {
        dispatchMessage(dispatch, payload?.message || 'Payment reversed', 'success');
        if (onReversed) {
          onReversed(payload);
        }
        onClose();
      })
      .catch((err) => {
        const message = typeof err === 'string' && err ? err : 'Unable to reverse this payment';
        dispatchMessage(dispatch, message, 'error');
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <Dialog open={!!open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Reverse payment</DialogTitle>
      <DialogContent>
        {payment ? (
          <Typography variant="body2" color="textSecondary" className="mb-16">
            {`${payment.payment_type || 'Payment'} of ${formatter.format(payment.paid_amount ?? payment.amount ?? 0)}`}
            {payment.date ? ` recorded on ${payment.date.split('T')[0]}` : ''}
          </Typography>
        ) : null}

        <Typography variant="subtitle2" className="mb-8">
          What happened to this payment?
        </Typography>
        <RadioGroup value={mode} onChange={(event) => setMode(event.target.value)}>
          <FormControlLabel
            value={MODE_MONEY_RETURNED}
            control={<Radio />}
            label={
              <Box>
                <Typography variant="body2">Money came back</Typography>
                <Typography variant="caption" color="textSecondary">
                  The payment stays in the history and a matching return is recorded next to it.
                  Use this whenever real money was received and then reversed.
                </Typography>
              </Box>
            }
          />
          <FormControlLabel
            value={MODE_NEVER_RECEIVED}
            control={<Radio />}
            label={
              <Box>
                <Typography variant="body2">Payment was never real</Typography>
                <Typography variant="caption" color="textSecondary">
                  The entry is hidden instead. Only for a keying error or a duplicate, where there
                  is nothing to show the customer.
                </Typography>
              </Box>
            }
          />
        </RadioGroup>

        <TextField
          label="Reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          fullWidth
          multiline
          rows={2}
          required
          className="mt-16"
          placeholder="For example: cheque returned unpaid by the bank"
        />

        <Alert severity="info" className="mt-16">
          The invoice goes back to Partial Payment if other payments remain, or Invoiced if none do,
          and returns to the active invoice list. Every order on it follows, unless the order is also
          billed on another invoice that is still paid in full.
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={submitting}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} color="secondary" variant="contained" disabled={!canSubmit}>
          {submitting ? 'Reversing…' : 'Reverse payment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default PaymentReversalDialog;
